import { trackMatomo } from '@shared/trackMatomo.js';
import { setErrorTicketId } from '../actions.js';
import type { MyStore } from '../store.js';

let store: MyStore;

export function setStore(s: MyStore): void {
  store = s;
}

(Error.prototype as any).toJSON = function toJSON() {
  return {
    name: this.name,
    message: this.message,
    fileName: this.fileName,
    lineNumber: this.lineNumber,
    columnNumber: this.columnNumber,
    description: this.description, // MS
    number: this.number, // MS
    stack: this.stack,
  };
};

window.addEventListener('error', (evt) => {
  // Benign: the browser only defers the notifications to the next frame.
  if (evt.message.startsWith('ResizeObserver loop')) {
    return;
  }

  sendError({
    kind: 'global',
    message: evt.message,
    filename: evt.filename,
    lineno: evt.lineno,
    colno: evt.colno,
    error: evt.error,
  });
});

interface ErrorDetails {
  kind: string;
  action?: unknown;
  error: unknown;
  message?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
}

// Matomo only needs a rate, not every occurrence (Sentry captures those). Dedupe
// identical errors and cap the total so a tight error loop can't emit thousands
// of events in a single visit.
const trackedErrorSignatures = new Set<string>();
let trackedErrorCount = 0;
const MAX_TRACKED_ERRORS = 25;

// filter out old browsers
const reportable = Boolean(Array.prototype.flatMap);

/**
 * Reports an error to Sentry and Matomo and returns the Sentry event id, if
 * any. This is the form for a caller that shows its own message; `sendError`
 * adds the generic ticket-id toast on top, for errors nothing else surfaces.
 */
export function reportError(errDetails: ErrorDetails): string | undefined {
  if (!reportable) {
    return undefined;
  }

  const { error, ...rest } = errDetails;

  console.error('Application error', rest, error);

  // Sentry's own global handler reports an error event without an error object
  // from its message; capturing the missing object would add a bare "null" event.
  const eventId =
    errDetails.kind === 'global' && error == null
      ? undefined
      : window.Sentry?.captureException(error);

  // Low-cardinality name (e.g. `TypeError`) makes the report diagnostic; the
  // full message is only used locally to dedupe, never sent.
  const name =
    error instanceof Error && error.name ? error.name : errDetails.kind;

  const signature = `${name}|${errDetails.message ?? (error instanceof Error ? error.message : '')}`;

  if (
    trackedErrorCount < MAX_TRACKED_ERRORS &&
    !trackedErrorSignatures.has(signature)
  ) {
    trackedErrorSignatures.add(signature);
    trackedErrorCount++;

    trackMatomo(['trackEvent', 'App', 'error', name]);
  }

  return eventId;
}

/** Reports an error and shows the generic ticket-id toast for it. */
export function sendError(errDetails: ErrorDetails): void {
  if (!reportable) {
    return;
  }

  const eventId = reportError(errDetails);

  if (errDetails.message === 'Script error.' || errDetails.filename === '') {
    // don't show to user
  } else {
    handle(eventId);
  }
}

// Set while the ticket id is being dispatched: an error that dispatch raises is
// still reported, but dispatching again would recurse until the stack overflows.
let handling = false;

function handle(id?: string) {
  if (store) {
    if (handling) {
      return;
    }

    handling = true;

    try {
      store.dispatch(setErrorTicketId(id));
    } finally {
      handling = false;
    }
  } else {
    document.body.innerHTML = `
      <h1>Application error</h1>
      <p>Ticket ID: ${id}.</p>
      <p>You can send ticket ID and steps how to reproduce the error to <a href="mailto:freemap@freemap.sk">freemap@freemap.sk</a>.</p>

      <h1>Chyba aplikácie</h1>
      <p>ID incidentu: ${id}.</p>
      <p>Na <a href="mailto:freemap@freemap.sk">freemap@freemap.sk</a> nám môžete zaslať toto ID a kroky, ako sa vám to podarilo.</p>

      <h1>Chyba aplikace</h1>
      <p>ID tiketu: ${id}.</p>
      <p>Na <a href="mailto:freemap@freemap.sk">freemap@freemap.sk</a> nám můžete zaslat toto ID a kroky vedoucí k chybě.</p>

      <h1>Błąd aplikacji</h1>
      <p>ID zgłoszenia: ${id}.</p>
      <p>Możesz wysłać ID zgłoszenia oraz kroki do odtworzenia błędu na adres <a href="mailto:freemap@freemap.sk">freemap@freemap.sk</a>.</p>

      <h1>Anwendungsfehler</h1>
      <p>Ticket-ID: ${id}.</p>
      <p>Sie können die Ticket-ID und die Schritte zur Reproduktion des Fehlers an <a href="mailto:freemap@freemap.sk">freemap@freemap.sk</a> senden.</p>

      <h1>Alkalmazáshiba</h1>
      <p>Jegyazonosító (ticket ID): ${id}.</p>
      <p>
        A jegyazonosítót és a hiba újbóli megjelenéséhez vezető lépések leírását a következő e-mail címre küldheti:
        <a href="mailto:freemap@freemap.sk">freemap@freemap.sk</a>.
      </p>

      <h1>Errore dell'applicazione</h1>
      <p>Ticket Nr.: ${id}.</p>
      <p>Puoi inviare il tuo numero di ticket e i passaggi per riprodurre l'errore a <a href="mailto:freemap@freemap.sk">freemap@freemap.sk</a>.</p>
    `;
  }
}
